
import React from "react";
import { useMemorial } from "@/contexts/MemorialContext";
import { useParams, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Cake, Cross, MapPin } from "lucide-react";
import { BirthInfoCard } from "./BirthInfoCard";
import { DeathInfoCard } from "./DeathInfoCard";
import { FamilyMembersCard } from "./FamilyMembersCard";
import { SectionHeader } from "../SectionHeader";
import { buildMemorialDisplayName } from "@/utils/memorialDisplayName";
import { formatDateForDisplay } from "@/utils/dateUtils";

export const AboutSection = () => {
  const { personalDetails } = useMemorial();
  const { id } = useParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const memorialIdFromQuery = searchParams.get('memorialId');
  const memorialId = id || memorialIdFromQuery;

  // Fetch memorial details directly to ensure we have the latest data
  const { data: memorialDetails } = useQuery({
    queryKey: ['memorialDetails', memorialId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('memorials_public')
        .select('*')
        .eq('id', memorialId)
        .maybeSingle();

      if (error) {
        console.error('[AboutSection] Error fetching memorial details:', error);
        throw error;
      }
      return data;
    },
    enabled: !!memorialId
  });

  // Use either the context data or the directly fetched data
  const displayData = {
    firstName: personalDetails.firstName || memorialDetails?.first_name || '',
    lastName: personalDetails.lastName || memorialDetails?.last_name || '',
    birthDate: personalDetails.birthDate || memorialDetails?.birth_date || '',
    birthPlace: personalDetails.birthPlace || memorialDetails?.place_of_birth || '',
    deathDate: personalDetails.deathDate || memorialDetails?.death_date || '',
    deathPlace: personalDetails.deathPlace || memorialDetails?.place_of_death || '',
    isDoubleMemorial: personalDetails.isDoubleMemorial || memorialDetails?.is_double_memorial || false,
    person2FirstName: personalDetails.person2FirstName || memorialDetails?.person2_first_name || '',
    person2LastName: personalDetails.person2LastName || memorialDetails?.person2_last_name || '',
    person2BirthDate: personalDetails.person2BirthDate || memorialDetails?.person2_birth_date || '',
    person2BirthPlace: personalDetails.person2BirthPlace || memorialDetails?.person2_place_of_birth || '',
    person2DeathDate: personalDetails.person2DeathDate || memorialDetails?.person2_death_date || '',
    person2DeathPlace: personalDetails.person2DeathPlace || memorialDetails?.person2_place_of_death || '',
  };

  const displayName = buildMemorialDisplayName({
    first_name: displayData.firstName,
    last_name: displayData.lastName,
    person2_first_name: displayData.person2FirstName,
    person2_last_name: displayData.person2LastName,
  });

  // For double memorials, use person labels on the cards
  const person1Label = displayData.isDoubleMemorial ? displayData.firstName : undefined;
  const person2Label = displayData.isDoubleMemorial ? displayData.person2FirstName : undefined;

  return (
    <div className="max-w-2xl mx-auto mb-12">
      <SectionHeader
        title={`About ${displayName}`}
        subtitle="Learn about their life, family, and the legacy they left behind"
      />

      <div className="space-y-6">
        {displayData.isDoubleMemorial ? (
          <>
            {/* Combined Birth card */}
            {(displayData.birthDate || displayData.birthPlace || displayData.person2BirthDate || displayData.person2BirthPlace) && (
              <div className="bg-card rounded-xl shadow-sm border overflow-hidden transition-all duration-300 hover:shadow-md">
                <div className="border-b bg-muted/30 px-6 py-4 flex items-center">
                  <Cake className="h-5 w-5 text-primary mr-2" />
                  <h3 className="font-medium text-foreground">Born</h3>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {(displayData.birthDate || displayData.birthPlace) && (
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-muted-foreground">{displayData.firstName}</p>
                        {displayData.birthDate && (
                          <p className="text-foreground">{formatDateForDisplay(displayData.birthDate)}</p>
                        )}
                        {displayData.birthPlace && (
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <MapPin className="h-4 w-4" />
                            <span>{displayData.birthPlace}</span>
                          </div>
                        )}
                      </div>
                    )}
                    {(displayData.person2BirthDate || displayData.person2BirthPlace) && (
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-muted-foreground">{displayData.person2FirstName}</p>
                        {displayData.person2BirthDate && (
                          <p className="text-foreground">{formatDateForDisplay(displayData.person2BirthDate)}</p>
                        )}
                        {displayData.person2BirthPlace && (
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <MapPin className="h-4 w-4" />
                            <span>{displayData.person2BirthPlace}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Combined Death card */}
            {(displayData.deathDate || displayData.deathPlace || displayData.person2DeathDate || displayData.person2DeathPlace) && (
              <div className="bg-card rounded-xl shadow-sm border overflow-hidden transition-all duration-300 hover:shadow-md">
                <div className="border-b bg-muted/30 px-6 py-4 flex items-center">
                  <Cross className="h-5 w-5 text-primary mr-2" />
                  <h3 className="font-medium text-foreground">Passed</h3>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {(displayData.deathDate || displayData.deathPlace) && (
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-muted-foreground">{displayData.firstName}</p>
                        {displayData.deathDate && (
                          <p className="text-foreground">{formatDateForDisplay(displayData.deathDate)}</p>
                        )}
                        {displayData.deathPlace && (
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <MapPin className="h-4 w-4" />
                            <span>{displayData.deathPlace}</span>
                          </div>
                        )}
                      </div>
                    )}
                    {(displayData.person2DeathDate || displayData.person2DeathPlace) && (
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-muted-foreground">{displayData.person2FirstName}</p>
                        {displayData.person2DeathDate && (
                          <p className="text-foreground">{formatDateForDisplay(displayData.person2DeathDate)}</p>
                        )}
                        {displayData.person2DeathPlace && (
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <MapPin className="h-4 w-4" />
                            <span>{displayData.person2DeathPlace}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            {/* Single memorial — existing cards */}
            <BirthInfoCard birthDate={displayData.birthDate} birthPlace={displayData.birthPlace} />
            <DeathInfoCard deathDate={displayData.deathDate} deathPlace={displayData.deathPlace} />
          </>
        )}

        {/* Family Section */}
        <FamilyMembersCard memorialId={memorialId || ''} />
      </div>
    </div>
  );
};
